import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Select, MenuItem, FormControl, InputLabel,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Card, CardContent
} from '@mui/material';
import { anomalyService } from '../services/anomalyService';

function AnomalyDashboard() {
  const [anomalies, setAnomalies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [resolveDialog, setResolveDialog] = useState({ open: false, id: null });
  const [resolveForm, setResolveForm] = useState({ status: 'resolved', notes: '' });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeverity, filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [anomalyData, statsData] = await Promise.all([
        anomalyService.listAnomalies({ severity: filterSeverity || undefined, resolution_status: filterStatus || undefined }),
        anomalyService.getStats(),
      ]);
      setAnomalies(anomalyData ?? []);
      setStats(statsData ?? {});
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    try {
      await anomalyService.resolveAnomaly(resolveDialog.id, resolveForm.status, resolveForm.notes);
      setResolveDialog({ open: false, id: null });
      setResolveForm({ status: 'resolved', notes: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const severityColor = (sev) => {
    const map = { critical: 'error', high: 'error', medium: 'warning', low: 'info', informational: 'default' };
    return map[sev] || 'default';
  };

  const statusColor = (st) => {
    const map = { flagged: 'warning', under_review: 'info', confirmed_anomaly: 'error', false_positive: 'default', resolved: 'success' };
    return map[st] || 'default';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Anomaly Detection</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Statistical outlier detection, zero emission flagging, and historical deviation analysis
      </Typography>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4">{stats.total}</Typography><Typography variant="body2" color="text.secondary">Total</Typography></CardContent></Card></Grid>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4" color="error.main">{stats.by_severity?.critical || 0}</Typography><Typography variant="body2" color="text.secondary">Critical</Typography></CardContent></Card></Grid>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4" color="error.light">{stats.by_severity?.high || 0}</Typography><Typography variant="body2" color="text.secondary">High</Typography></CardContent></Card></Grid>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4" color="warning.main">{stats.by_severity?.medium || 0}</Typography><Typography variant="body2" color="text.secondary">Medium</Typography></CardContent></Card></Grid>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4" color="info.main">{stats.by_status?.flagged || 0}</Typography><Typography variant="body2" color="text.secondary">Flagged</Typography></CardContent></Card></Grid>
          <Grid item xs={2}><Card><CardContent><Typography variant="h4" color="success.main">{stats.by_status?.resolved || 0}</Typography><Typography variant="body2" color="text.secondary">Resolved</Typography></CardContent></Card></Grid>
        </Grid>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={filterSeverity} label="Severity" onChange={(e) => setFilterSeverity(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="flagged">Flagged</MenuItem>
            <MenuItem value="under_review">Under Review</MenuItem>
            <MenuItem value="confirmed_anomaly">Confirmed</MenuItem>
            <MenuItem value="false_positive">False Positive</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Detected</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {anomalies.map((a) => (
                <TableRow key={a.anomaly_detection_id}>
                  <TableCell>{new Date(a.detected_at).toLocaleString()}</TableCell>
                  <TableCell><Chip label={a.detection_method} size="small" variant="outlined" /></TableCell>
                  <TableCell><Chip label={a.severity} size="small" color={severityColor(a.severity)} /></TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>{a.anomaly_description}</TableCell>
                  <TableCell><Chip label={a.resolution_status} size="small" color={statusColor(a.resolution_status)} /></TableCell>
                  <TableCell>
                    {a.resolution_status === 'flagged' && (
                      <Button size="small" onClick={() => setResolveDialog({ open: true, id: a.anomaly_detection_id })}>
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {anomalies.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No anomalies detected</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={resolveDialog.open} onClose={() => setResolveDialog({ open: false, id: null })}>
        <DialogTitle>Resolve Anomaly</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Resolution Status</InputLabel>
            <Select value={resolveForm.status} label="Resolution Status" onChange={(e) => setResolveForm({ ...resolveForm, status: e.target.value })}>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="false_positive">False Positive</MenuItem>
              <MenuItem value="confirmed_anomaly">Confirmed Anomaly</MenuItem>
              <MenuItem value="under_review">Under Review</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={3} label="Resolution Notes" value={resolveForm.notes} onChange={(e) => setResolveForm({ ...resolveForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, id: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleResolve}>Resolve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AnomalyDashboard;
